import { useIntl } from 'react-intl';
import { graphql } from 'react-relay';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import type { useIsImportedPageQuery$data as UseIsImportedPageQueryData } from './__generated__/useIsImportedPageQuery.graphql.ts';
import { IMPORT_FOLDER_NAMES } from './importFolderNames.ts';

// Show the banner if the page was created within this many milliseconds.
const RECENCY_WINDOW_MS = 24 * 60 * 60 * 1000; // 24 hours

const UseIsImportedPageQuery = graphql`
	query useIsImportedPageQuery($contentId: ID) {
		singleContent(id: $contentId) {
			ancestors {
				title
			}
			history {
				createdDate
			}
		}
	}
`;

/**
 * Returns true if the given page appears to have been recently imported:
 * - One of its ancestors has a title matching a known import folder name
 *   (resolved in the current locale — handles translated folder names)
 * - AND the page was created within the last 24 hours
 */
export const useIsImportedPage = (contentId: string): boolean => {
	const { formatMessage } = useIntl();

	// Resolve folder names in the current locale so we match translated titles correctly.
	const importFolderTitles = new Set(
		Object.values(IMPORT_FOLDER_NAMES).map((descriptor) => formatMessage(descriptor)),
	);

	const { data, loading } = useQueryInterop<UseIsImportedPageQueryData>(UseIsImportedPageQuery, {
		variables: { contentId },
		skip: !contentId,
	});

	if (loading || !data?.singleContent) {
		return false;
	}

	const { ancestors, history } = data.singleContent;

	// Check if any ancestor is an import folder (locale-aware comparison)
	const hasImportFolderAncestor = ancestors?.some(
		(ancestor) => ancestor?.title && importFolderTitles.has(ancestor.title),
	);

	if (!hasImportFolderAncestor) {
		return false;
	}

	// Check if page was created recently
	const createdDate = history?.createdDate;
	if (!createdDate) {
		return false;
	}

	const ageMs = Date.now() - new Date(createdDate).getTime();
	return ageMs <= RECENCY_WINDOW_MS;
};
